import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Moon, ChevronLeft, ChevronRight } from 'lucide-react';

// Hijri month names in Albanian
const hijriMonths = [
  "Muharrem", "Safer", "Rebi'ul-Evvel", "Rebi'ul-Ahir",
  "Xhumadel-Ula", "Xhumadel-Ahire", "Rexheb", "Sha'ban",
  "Ramazan", "Shevval", "Dhul-Ka'de", "Dhul-Hixhe"
];

const gregorianMonths = [
  "Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
  "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"
];

const weekDays = ["Hën", "Mar", "Mër", "Enj", "Pre", "Sht", "Die"];

// Important Islamic dates (Hijri month, day)
const islamicEvents = {
  "1-1": "Viti i Ri Hixhri",
  "1-10": "Ashura",
  "3-12": "Mevludi (lindja e Profetit ﷺ)",
  "7-27": "Isra dhe Mi'raxh",
  "8-15": "Nata e Beratit",
  "9-1": "Fillimi i Ramazanit",
  "9-27": "Nata e Kadrit",
  "10-1": "Fitër Bajrami",
  "12-10": "Kurban Bajrami",
  "12-9": "Dita e Arafatit"
};

const IslamicCalendar = ({ isOpen, onClose }) => {
  const [hijriDate, setHijriDate] = useState(null);
  const [gregorianDate, setGregorianDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  // Fetch Hijri date from API
  useEffect(() => {
    if (!isOpen) return;
    fetchHijriDate();
  }, [isOpen]);

  const fetchHijriDate = async () => {
    try {
      const today = new Date();
      const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
      const response = await fetch(`https://api.aladhan.com/v1/gpiToH/${dateStr}`, {
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      const data = await response.json();
      
      if (data.code === 200) {
        setHijriDate(data.data.hijri);
        calculateUpcomingEvents(data.data.hijri);
      } else {
        // Fallback to approximate Hijri date
        setFallbackHijriDate();
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Hijri date:", error);
      // Fallback to approximate Hijri date
      setFallbackHijriDate();
      setLoading(false);
    }
  };

  const setFallbackHijriDate = () => {
    // Approximate calculation - not precise but gives reasonable estimate
    // March 6, 2026 is approximately 7 Sha'ban 1447
    const today = new Date();
    const baseDate = new Date(2026, 2, 6); // March 6, 2026
    const baseHijri = { day: 7, month: 8, year: 1447 }; // 7 Sha'ban 1447
    
    const diffDays = Math.floor((today - baseDate) / (1000 * 60 * 60 * 24));
    let hijriDay = baseHijri.day + diffDays;
    let hijriMonth = baseHijri.month;
    let hijriYear = baseHijri.year;
    
    // Simple month overflow handling (Hijri months alternate 29-30 days)
    while (hijriDay > 30) {
      hijriDay -= 30;
      hijriMonth++;
      if (hijriMonth > 12) {
        hijriMonth = 1;
        hijriYear++;
      }
    }
    
    setHijriDate({
      day: String(Math.max(1, hijriDay)),
      month: { number: String(hijriMonth) },
      year: String(hijriYear)
    });
    
    // Set some default upcoming events
    setUpcomingEvents([
      { name: "Fillimi i Ramazanit", hijriDate: "1 Ramazan", daysAway: "~24" },
      { name: "Nata e Kadrit", hijriDate: "27 Ramazan", daysAway: "~50" }
    ]);
  };

  const calculateUpcomingEvents = (hijri) => {
    const currentMonth = parseInt(hijri.month.number);
    const currentDay = parseInt(hijri.day);
    
    const events = [];
    
    // Check events in current and next 3 months
    for (let monthOffset = 0; monthOffset < 4; monthOffset++) {
      const checkMonth = ((currentMonth - 1 + monthOffset) % 12) + 1;
      
      Object.entries(islamicEvents).forEach(([key, name]) => {
        const [month, day] = key.split('-').map(Number);
        if (month === checkMonth) {
          if (monthOffset === 0 && day < currentDay) return; // Skip past events this month
          
          events.push({
            name,
            hijriDate: `${day} ${hijriMonths[month - 1]}`,
            daysAway: monthOffset === 0 ? day - currentDay : `~${monthOffset * 30 + day - currentDay}`
          });
        }
      });
    }
    
    setUpcomingEvents(events.slice(0, 5));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = (firstDay.getDay() + 6) % 7; // Adjust for Monday start
    
    const days = [];
    
    // Empty cells for days before the 1st
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentMonth.getMonth() === today.getMonth() && 
           currentMonth.getFullYear() === today.getFullYear();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="islamic-calendar-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="islamic-calendar-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary-600 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-heading font-bold">Kalendari Islamik</h2>
                <p className="text-white/70 text-sm">Data Hixhri dhe Gregoriane</p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
                data-testid="islamic-calendar-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Hijri Date Display */}
            {!loading && hijriDate && (
              <div className="bg-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Moon className="w-8 h-8 text-accent" />
                  <div>
                    <p className="text-3xl font-heading font-bold">
                      {hijriDate.day} {hijriMonths[parseInt(hijriDate.month.number) - 1]} {hijriDate.year}
                    </p>
                    <p className="text-white/70 text-sm">
                      {gregorianDate.getDate()} {gregorianMonths[gregorianDate.getMonth()]} {gregorianDate.getFullYear()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Duke ngarkuar...</p>
              </div>
            ) : (
              <>
                {/* Gregorian Calendar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => navigateMonth(-1)}
                      className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                      data-testid="calendar-prev-month"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h3 className="text-lg font-heading font-bold text-primary">
                      {gregorianMonths[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h3>
                    <button
                      onClick={() => navigateMonth(1)}
                      className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                      data-testid="calendar-next-month"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Week Days Header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekDays.map((day) => (
                      <div key={day} className="text-center text-sm font-semibold text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((day, index) => (
                      <div
                        key={index}
                        className={`aspect-square flex items-center justify-center text-sm rounded-lg ${
                          day === null
                            ? ''
                            : isToday(day)
                            ? 'bg-primary text-white font-bold'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upcoming Islamic Events */}
                <div>
                  <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Ngjarje të Ardhshme Islamike
                  </h3>
                  
                  {upcomingEvents.length > 0 ? (
                    <div className="space-y-3">
                      {upcomingEvents.map((event, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl"
                        >
                          <div>
                            <p className="font-semibold text-gray-800">{event.name}</p>
                            <p className="text-sm text-gray-500">{event.hijriDate}</p>
                          </div>
                          <span className="text-sm bg-accent/20 text-accent px-3 py-1 rounded-full font-medium">
                            {typeof event.daysAway === 'number' 
                              ? event.daysAway === 0 
                                ? 'Sot!' 
                                : `${event.daysAway} ditë`
                              : event.daysAway + ' ditë'
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Nuk ka ngjarje të ardhshme</p>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default IslamicCalendar;
